import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AdminFormPage,
  saveErrorToast,
  useAdminFormPersistence,
} from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useV2GetWebhookSubscriptionQuery } from "@generated";
import { WebhookSubscriptionForm } from "../WebhookSubscriptionForm/WebhookSubscriptionForm";
import { PublicationsTab } from "../Publications/PublicationsTab";
import { useWebhookSubscriptionMutations } from "../api/useWebhookSubscriptionMutations";
import { emptyWebhookSubscriptionForm } from "../types";
import type { EventType, WebhookSubscriptionFormValues } from "../types";

const FORM_ID = "webhook-subscription-form";
type Tab = "subscription" | "publications";

const WEBHOOK_EDIT_TABS: { key: Tab; label: string }[] = [
  { key: "subscription", label: "Subscription" },
  { key: "publications", label: "Publications" },
];

export const Edit = (): JSX.Element => {
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const [activeTab, setActiveTab] = useState<Tab>("subscription");
  const { update } = useWebhookSubscriptionMutations();
  const { data, isLoading, refetch } = useV2GetWebhookSubscriptionQuery(
    { id: id },
    { refetchOnMountOrArgChange: true }
  );
  const { toast, setToast, savedInitial, formKey, markSaved } =
    useAdminFormPersistence<WebhookSubscriptionFormValues>();
  const [submitting, setSubmitting] = useState(false);

  const loadedInitial = useMemo<WebhookSubscriptionFormValues>(() => {
    const attrs = data?.data?.attributes;
    if (!attrs) return emptyWebhookSubscriptionForm;
    return {
      webhookUrl: attrs["webhook-url"] ?? "",
      eventType: (attrs["event-type"] ?? "") as EventType | "",
    };
  }, [data]);

  if (isLoading) return <ScopeStackSpinner />;

  const initialValues = savedInitial ?? loadedInitial;

  const handleSubmit = async (values: WebhookSubscriptionFormValues) => {
    setSubmitting(true);
    try {
      await update(id, values);
      markSaved(values, "Webhook subscription saved");
      refetch();
    } catch (err) {
      setToast(saveErrorToast(err, "subscription"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="webhook-edit-layout">
      <AdminFormPage
        title="Edit Webhook Subscription"
        formId={activeTab === "subscription" ? FORM_ID : undefined}
        onCancel={() => navigate("../..", { relative: "path" })}
        isSubmitting={submitting}
        tabs={WEBHOOK_EDIT_TABS}
        activeTab={activeTab}
        onTabChange={(key) => setActiveTab(key as Tab)}
      >
        {activeTab === "subscription" && (
          <WebhookSubscriptionForm
            key={formKey}
            id={FORM_ID}
            initialValues={initialValues}
            isSubmitting={submitting}
            onSubmit={handleSubmit}
          />
        )}
        {activeTab === "publications" && (
          <PublicationsTab webhookSubscriptionId={id} />
        )}
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};
